import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Download, ExternalLink, ListOrdered, Pencil, Sparkles, Trash2, X } from 'lucide-react';
import type { VaultDocumentDto } from '../../api/cms';
import { fetchVaultFileBlob } from '../../api/vault';
import { pdfFirstPageToBlob } from '../../lib/pdfConvert';
import { formatSequencePosition } from '../../lib/vaultSequence';
import { fieldDisplayValue, formatConfidence } from '../../types/documentIntelligence';
import type { DocumentAnalysisResult } from '../../types/documentIntelligence';

interface VaultPreviewModalProps {
  document: VaultDocumentDto | null;
  onClose: () => void;
  onDownload: (doc: VaultDocumentDto) => void;
  onRename: (doc: VaultDocumentDto) => void;
  onReplace: (doc: VaultDocumentDto) => void;
  onSetSequence?: (doc: VaultDocumentDto) => void;
  onDelete: (doc: VaultDocumentDto) => void;
}

function useScrollArrows(deps: unknown[] = []) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 4);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measure when panel content changes
  }, [updateScrollState, ...deps]);

  const scrollBy = (direction: 'up' | 'down') => {
    scrollRef.current?.scrollBy({
      top: direction === 'up' ? -180 : 180,
      behavior: 'smooth',
    });
  };

  return { scrollRef, canScrollUp, canScrollDown, scrollBy, updateScrollState };
}

function ScrollArrowNav({
  canScrollUp,
  canScrollDown,
  onScroll,
  upLabel,
  downLabel,
}: {
  canScrollUp: boolean;
  canScrollDown: boolean;
  onScroll: (direction: 'up' | 'down') => void;
  upLabel: string;
  downLabel: string;
}) {
  return (
    <div className="wfx-vault-preview-meta-nav">
      <button
        type="button"
        className="ghost-icon-button wfx-vault-scroll-arrow"
        aria-label={upLabel}
        disabled={!canScrollUp}
        onClick={() => onScroll('up')}
      >
        <ChevronUp size={18} />
      </button>
      <button
        type="button"
        className="ghost-icon-button wfx-vault-scroll-arrow"
        aria-label={downLabel}
        disabled={!canScrollDown}
        onClick={() => onScroll('down')}
      >
        <ChevronDown size={18} />
      </button>
    </div>
  );
}

function MetadataPanel({ analysis }: { analysis: DocumentAnalysisResult }) {
  const { scrollRef, canScrollUp, canScrollDown, scrollBy } = useScrollArrows([analysis]);

  const rows = [
    analysis?.eventName?.value ? { label: 'Event', value: fieldDisplayValue(analysis.eventName) } : null,
    analysis?.organizer?.value ? { label: 'Organizer', value: fieldDisplayValue(analysis.organizer) } : null,
    analysis?.recognition?.value ? { label: 'Recognition', value: fieldDisplayValue(analysis.recognition) } : null,
    analysis?.eventDate?.value ? { label: 'Event date', value: fieldDisplayValue(analysis.eventDate) } : null,
    analysis?.issueDate?.value ? { label: 'Issue date', value: fieldDisplayValue(analysis.issueDate) } : null,
    analysis?.location?.value ? { label: 'Location', value: fieldDisplayValue(analysis.location) } : null,
    analysis?.level?.value ? { label: 'Level', value: fieldDisplayValue(analysis.level) } : null,
    analysis?.research?.title?.value ? { label: 'Research title', value: fieldDisplayValue(analysis.research.title) } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (rows.length === 0 && !analysis?.description?.value) return null;

  return (
    <aside className="wfx-vault-preview-meta-wrap">
      <div className="wfx-vault-preview-meta-head">
        <h3>Document Details</h3>
        <ScrollArrowNav
          canScrollUp={canScrollUp}
          canScrollDown={canScrollDown}
          onScroll={scrollBy}
          upLabel="Scroll details up"
          downLabel="Scroll details down"
        />
      </div>
      <div ref={scrollRef} className="wfx-vault-preview-meta glass-surface">
        <div className="wfx-ai-review-summary">
          <Sparkles size={16} aria-hidden="true" />
          <div>
            <strong>AI metadata</strong>
            <p className="wfx-muted">{formatConfidence(analysis?.overallConfidence ?? 0)} confidence</p>
          </div>
        </div>
        {analysis?.description?.value && <p>{analysis.description.value}</p>}
        {rows.length > 0 && (
          <dl className="wfx-vault-meta-list">
            {rows.map((row) => (
              <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {analysis?.tags?.value && analysis.tags.value.length > 0 && (
          <div className="wfx-vault-meta-tags">
            {analysis.tags.value.map((tag) => (
              <span key={tag} className="chip-pill">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function DocumentPreviewPanel({
  previewUrl,
  name,
  loading,
  error,
  hasVisualPreview,
  fallbackMessage,
}: {
  previewUrl: string | null;
  name: string;
  loading: boolean;
  error: string | null;
  hasVisualPreview: boolean;
  fallbackMessage?: string;
}) {
  const { scrollRef, canScrollUp, canScrollDown, scrollBy, updateScrollState } = useScrollArrows([
    previewUrl,
    loading,
    error,
    hasVisualPreview,
  ]);

  return (
    <div className="wfx-vault-preview-doc-wrap">
      <div className="wfx-vault-preview-doc-head">
        <h3>Document</h3>
        {hasVisualPreview && (
          <ScrollArrowNav
            canScrollUp={canScrollUp}
            canScrollDown={canScrollDown}
            onScroll={scrollBy}
            upLabel="Scroll document up"
            downLabel="Scroll document down"
          />
        )}
      </div>
      <div ref={scrollRef} className="wfx-vault-preview-body glass-surface">
        {loading && <p className="wfx-muted wfx-vault-preview-placeholder">Loading preview…</p>}
        {error && <div className="auth-error" role="alert">{error}</div>}
        {!loading && !error && hasVisualPreview && previewUrl && (
          <img
            src={previewUrl}
            alt={name}
            className="wfx-vault-preview-image"
            onLoad={updateScrollState}
          />
        )}
        {!loading && !error && !hasVisualPreview && fallbackMessage && (
          <p className="wfx-muted wfx-vault-preview-placeholder">{fallbackMessage}</p>
        )}
      </div>
    </div>
  );
}

export function VaultPreviewModal({
  document,
  onClose,
  onDownload,
  onRename,
  onReplace,
  onSetSequence,
  onDelete,
}: VaultPreviewModalProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!document) {
      setBlobUrl(null);
      setPreviewUrl(null);
      return;
    }

    if (document.fileAvailable === false) {
      setBlobUrl(null);
      setPreviewUrl(null);
      setLoading(false);
      setError('This file is no longer available. Use Replace to upload it again.');
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    let renderedPreviewUrl: string | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const blob = await fetchVaultFileBlob(document.id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);

        if (document.mimeType === 'application/pdf') {
          const pageBlob = await pdfFirstPageToBlob(blob);
          if (cancelled) return;
          renderedPreviewUrl = URL.createObjectURL(pageBlob);
          setPreviewUrl(renderedPreviewUrl);
        } else if (document.mimeType?.startsWith('image/')) {
          setPreviewUrl(objectUrl);
        } else {
          setPreviewUrl(null);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (renderedPreviewUrl && renderedPreviewUrl !== objectUrl) {
        URL.revokeObjectURL(renderedPreviewUrl);
      }
    };
  }, [document]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!document) return null;

  const isImage = document.mimeType?.startsWith('image/');
  const isPdf = document.mimeType === 'application/pdf';
  const analysis = document.aiMetadata as DocumentAnalysisResult | undefined;
  const hasVisualPreview = Boolean(previewUrl && (isImage || isPdf));

  return (
    <div className="wfx-vault-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-vault-modal wfx-vault-preview glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-vault-modal-head">
          <div>
            <span className="chip-pill">{document.detectedDocType || document.subcategory}</span>
            <h2 id="vault-preview-title">{document.name}</h2>
            <p className="wfx-muted">
              {document.fileSize} · v{document.version} · Uploaded {document.uploadedAt}
              {typeof document.sequenceOrder === 'number' && document.sequenceOrder > 0 && (
                <> · {formatSequencePosition(document.sequenceOrder)} in sequence</>
              )}
            </p>
            {document.description && <p className="wfx-vault-preview-desc">{document.description}</p>}
          </div>
          <button type="button" className="ghost-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="wfx-vault-preview-layout">
          <DocumentPreviewPanel
            previewUrl={previewUrl}
            name={document.name}
            loading={loading}
            error={error}
            hasVisualPreview={hasVisualPreview}
            fallbackMessage={
              !loading && !error && blobUrl && !hasVisualPreview
                ? 'Preview not available for this file type. Download to view.'
                : undefined
            }
          />
          {analysis ? <MetadataPanel analysis={analysis} /> : null}
        </div>

        <div className="wfx-vault-modal-actions">
          <button
            type="button"
            className="primary-action compact wfx-vault-preview-btn-primary"
            onClick={() => onDownload(document)}
          >
            <Download size={16} /> Download
          </button>
          <button
            type="button"
            className="secondary-action compact wfx-vault-preview-btn"
            onClick={() => onRename(document)}
          >
            <Pencil size={15} /> Rename
          </button>
          {onSetSequence && (
            <button
              type="button"
              className="secondary-action compact wfx-vault-preview-btn"
              onClick={() => onSetSequence(document)}
            >
              <ListOrdered size={15} /> Set position
            </button>
          )}
          <button
            type="button"
            className="secondary-action compact wfx-vault-preview-btn"
            onClick={() => onReplace(document)}
          >
            <ExternalLink size={15} /> Replace
          </button>
          <button
            type="button"
            className="secondary-action compact wfx-danger-action wfx-vault-preview-btn"
            onClick={() => onDelete(document)}
          >
            <Trash2 size={15} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
